import { createContext, useContext, useState, useEffect } from "react";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  // Initialize from localStorage to prevent extra renders
  const [cart, setCart] = useState([]);
  const [price, setPrice] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [count, setCount] = useState(null);

  // Load cart from localStorage when component mounts
  useEffect(() => {
    try {
      const storedCart = localStorage.getItem("cart");
      const storedPrice = localStorage.getItem("price");

      if (storedCart) setCart(JSON.parse(storedCart));
      if (storedPrice) setPrice(Number(storedPrice));
    } catch (error) {
      console.error("Error loading from localStorage:", error);
    }

    setIsLoaded(true); // Mark as loaded to prevent overwriting localStorage
  }, []);

  // Save cart to localStorage whenever cart or price changes
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem("cart", JSON.stringify(cart));
      localStorage.setItem("price", price);
    }
  }, [cart, price, isLoaded]);

  const addToCart = (item, quantity) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((cartItem) => cartItem.id === item.id);
      let newCart;
      if (existingItem) {
        newCart = prevCart.map((cartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + quantity }
            : cartItem
        );
      } else {
        newCart = [...prevCart, { ...item, quantity }];
      }

      // Calculate the new total count of items in the cart
      const newCount = newCart.reduce((acc, cartItem) => acc + cartItem.quantity, 0);
      setCount(newCount);

      return newCart;
    });

    setPrice((prevPrice) => prevPrice + item.price * quantity);
  };

  const removeFromCart = (id) => {
    setCart((prevCart) => {
      const newCart = prevCart.filter((item) => item.id !== id);
  
      // Recalculate price from newCart
      const newPrice = newCart.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0
      );
      setPrice(newPrice);
  
      // Recalculate count from newCart
      const newCount = newCart.reduce((acc, item) => acc + item.quantity, 0);
      setCount(newCount);
  
      return newCart;
    });
  };

  const changeCart = (item, quantity) => {
    setCart((prevCart) => {
      const newCart = prevCart.map((cartItem) =>
        cartItem.id === item.id ? { ...cartItem, quantity } : cartItem
      );

      // Calculate the new total count of items in the cart
      const newCount = newCart.reduce((acc, cartItem) => acc + cartItem.quantity, 0);
      setCount(newCount);

      return newCart;
    });

    setPrice((prevPrice) => {
      const existingItem = cart.find((cartItem) => cartItem.id === item.id);
      if (existingItem) {
        const oldTotal = existingItem.quantity * existingItem.price;
        const newTotal = quantity * existingItem.price;
        return prevPrice - oldTotal + newTotal;
      }
      return prevPrice;
    });
  };

  const deleteAllCart = () => {
    setCart([]);
    setPrice(0);
    setCount(0);  // Reset the count
    localStorage.removeItem("cart");
    localStorage.removeItem("price");
  };

  return (
    <CartContext.Provider value={{ cart, price, count, addToCart, removeFromCart, changeCart, deleteAllCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
